import { z } from 'zod'
import i18n from '@/i18n'

const requiredNumber = (label: string, min: number, max: number) =>
  z
    .string()
    .trim()
    .min(1, `Enter ${label}.`)
    .transform(Number)
    .refine((v) => Number.isFinite(v) && v >= min && v <= max, `Enter a number between ${min} and ${max}.`)

const optionalNumber = (min: number, max: number) =>
  z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : Number(v)))
    .nullable()
    .refine(
      (v) => v === null || (Number.isFinite(v) && v >= min && v <= max),
      `Enter a number between ${min} and ${max}.`,
    )

export const examSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter a name, e.g. "Midterm".').max(150, 'Use at most 150 characters.'),
    examTypeId: z.string().min(1, 'Choose an exam type.').transform(Number),
    gradingScaleId: z
      .string()
      .transform((v) => (v === '' ? null : Number(v)))
      .nullable(),
    academicYearId: z
      .string()
      .transform((v) => (v === '' ? null : Number(v)))
      .nullable(),
    startsOn: z.string().min(1, 'Choose the first day.'),
    endsOn: z.string().min(1, 'Choose the last day.'),
    weightPercent: optionalNumber(0, 100),
    classIds: z.array(z.number()).min(1, 'Choose at least one class.'),
  })
  .refine((v) => !v.startsOn || !v.endsOn || v.endsOn >= v.startsOn, {
    path: ['endsOn'],
    message: 'The last day must be on or after the first day.',
  })

export type ExamFormInput = z.input<typeof examSchema>
export type ExamFormOutput = z.output<typeof examSchema>

export const paperSchema = z
  .object({
    classId: z.number().nullable(),
    subjectId: z.number().nullable(),
    date: z
      .string()
      .transform((v) => (v === '' ? null : v))
      .nullable(),
    maxMarks: requiredNumber('the maximum marks', 1, 1000),
    passMarks: requiredNumber('the pass marks', 0, 1000),
  })
  .refine((v) => v.passMarks <= v.maxMarks, {
    path: ['passMarks'],
    message: 'Pass marks cannot exceed the maximum.',
  })

export type PaperFormInput = z.input<typeof paperSchema>
export type PaperFormOutput = z.output<typeof paperSchema>

const bandSchema = z
  .object({
    label: z.string().trim().min(1, 'Label?').max(30, 'Max 30 chars.'),
    minPercent: requiredNumber('a lower bound', 0, 100),
    maxPercent: requiredNumber('an upper bound', 0, 100),
    gradePoint: optionalNumber(0, 10),
    isPass: z.boolean(),
  })
  .refine((b) => b.maxPercent >= b.minPercent, { path: ['maxPercent'], message: 'Below the lower bound.' })

/**
 * Mirrors the server rule: bands sorted by lower bound must start at 0, follow each other at
 * +0.01 and end at 100. Returns a human message, or null when coverage is complete.
 */
export function coverageProblem(bands: { minPercent: number; maxPercent: number }[]): string | null {
  const cents = bands
    .map((b) => [Math.round(b.minPercent * 100), Math.round(b.maxPercent * 100)] as const)
    .sort((a, b) => a[0] - b[0])
  const pct = (c: number) => String(Number((c / 100).toFixed(2)))
  let expected = 0
  for (const [min, max] of cents) {
    if (min > expected)
      return i18n.t('No grade covers {{from}}% to {{to}}%.', {
        ns: 'exams',
        from: pct(expected),
        to: pct(min - 1),
      })
    if (min < expected)
      return i18n.t('Grade ranges overlap around {{value}}%.', { ns: 'exams', value: pct(min) })
    expected = max + 1
  }
  return expected === 10001
    ? null
    : i18n.t('No grade covers {{from}}% to {{to}}%.', {
        ns: 'exams',
        from: pct(Math.min(expected, 10000)),
        to: '100',
      })
}

export const gradingScaleSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter a name.').max(100, 'Use at most 100 characters.'),
    isDefault: z.boolean(),
    bands: z.array(bandSchema).min(1, 'Add at least one grade.').max(20, 'Use at most 20 grades.'),
    /** Show results as a percentage, or as marks out of N with coefficient-weighted averages. */
    resultMode: z.enum(['percentage', 'outOf']).default('percentage'),
    outOf: z.string().trim().default(''),
  })
  .superRefine((v, ctx) => {
    if (v.resultMode === 'outOf' && !/^\d+$/.test(v.outOf)) {
      ctx.addIssue({ code: 'custom', path: ['outOf'], message: 'Enter a whole number, e.g. 20.' })
    } else if (v.resultMode === 'outOf' && (Number(v.outOf) < 1 || Number(v.outOf) > 1000)) {
      ctx.addIssue({ code: 'custom', path: ['outOf'], message: 'Enter a number between 1 and 1000.' })
    }
    const labels = v.bands.map((b) => b.label.trim().toLowerCase())
    if (new Set(labels).size !== labels.length)
      ctx.addIssue({ code: 'custom', path: ['bands'], message: 'Each grade label must be unique.' })
    const problem = coverageProblem(v.bands)
    if (problem) ctx.addIssue({ code: 'custom', path: ['bands'], message: problem })
  })
  .transform(({ resultMode, outOf, ...rest }) => ({
    ...rest,
    outOf: resultMode === 'outOf' ? Number(outOf) : null,
  }))

export type GradingScaleFormInput = z.input<typeof gradingScaleSchema>
export type GradingScaleFormOutput = z.output<typeof gradingScaleSchema>

/** Parses a mark typed in the grid. Empty = not entered. */
export function parseMark(raw: string, max: number): { value: number | null; error: string | null } {
  const text = raw.trim().replace(',', '.')
  if (text === '') return { value: null, error: null }
  const value = Number(text)
  if (!Number.isFinite(value)) return { value: null, error: i18n.t('Not a number', { ns: 'exams' }) }
  if (value < 0) return { value, error: i18n.t('Cannot be negative', { ns: 'exams' }) }
  if (value > max) return { value, error: i18n.t('Max is {{max}}', { ns: 'exams', max }) }
  if (Math.round(value * 100) !== value * 100)
    return { value, error: i18n.t('Use at most 2 decimals', { ns: 'exams' }) }
  return { value, error: null }
}
